import { Action } from 'redux';
import IDocument from '~Api/Loan/IDocument';
import IHistory from '~Api/Loan/IHistory';
import ILoan from '~Api/Loan/ILoan';
import ILoanFee from '~Api/Loan/ILoanFee';
import ILoanGracePeriod from '~Api/Loan/ILoanGracePeriod';
import ILoanNote from '~Api/Loan/ILoanNote';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import ILoanPayoutFigureItem from '~Api/Loan/ILoanPayoutFigureItem';
import ILoanPayoutFigureSection from '~Api/Loan/ILoanPayoutFigureSection';
import ILoanTransaction from '~Api/Loan/ILoanTransaction';
import MaturityReminderStatusEnum from '~Api/Loan/MaturityReminderStatusEnum';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import LoansActionsEnum from './ActionsEnum';
import { ILoanListSettings } from './List';
import LoanStatusEnum from '~Api/Loan/LoanStatusEnum';
import ListSortOrderEnum from '~Api/Loan/ListSortOrderEnum';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ILoanDrawdownItem from '~Api/Loan/ILoanDrawdownItem';
import IInvestment from '~Api/Investment/IInvestment';
import ILoanPayoutFigureCalculation from '~Api/Loan/ILoanPayoutFigureCalculation';

export interface ILoanActiveAction extends Action {
    uuid: string;
}

export function loanActiveAction(uuid: string): ILoanActiveAction {
    return {
        type: LoansActionsEnum.LoanActive,
        uuid,
    };
}

export interface ILoanAssignAction extends Action {
    administratorUuid: string;
    loanUuid: string;
}

export function loanAssignAction(loanUuid: string, administratorUuid: string): ILoanAssignAction {
    return {
        administratorUuid,
        loanUuid,
        type: LoansActionsEnum.LoanAssign,
    };
}

export interface ILoanDischargeAction extends Action {
    dischargeDate: string;
    uuid: string;
}

export function loanDischargeAction(uuid: string, dischargeDate: string): ILoanDischargeAction {
    return {
        dischargeDate,
        type: LoansActionsEnum.LoanDischarge,
        uuid,
    };
}

export interface ILoanDischargeDateAction extends Action {
    dischargeDate: string;
    uuid: string;
}

export function loanDischargeDateAction(uuid: string, dischargeDate: string): ILoanDischargeDateAction {
    return {
        dischargeDate,
        type: LoansActionsEnum.LoanDischargeDate,
        uuid,
    };
}

export interface ILoanDocumentsAddAction extends Action {
    file: File;
    loanUuid: string;
}

export function loanDocumentsAddAction(loanUuid: string, file: File): ILoanDocumentsAddAction {
    return {
        file,
        loanUuid,
        type: LoansActionsEnum.LoanDocumentsAdd,
    };
}

export interface ILoanDocumentsListAction extends Action {
    loanUuid: string;
}

export function loanDocumentsListAction(loanUuid: string): ILoanDocumentsListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanDocumentsList,
    };
}

export interface ILoanDocumentsSetAction extends Action {
    documents: IDocument[];
    loanUuid: string;
}

export function loanDocumentsSetAction(loanUuid: string, documents: IDocument[]): ILoanDocumentsSetAction {
    return {
        documents,
        loanUuid,
        type: LoansActionsEnum.LoanDocumentsSet,
    };
}

export interface ILoanDocumentSetAction extends Action {
    document: IDocument;
}

export function loanDocumentSetAction(document: IDocument): ILoanDocumentSetAction {
    return {
        document,
        type: LoansActionsEnum.LoanDocumentSet,
    };
}

export interface ILoanDocumentDeleteAction extends Action {
    documentUuid: string;
}

export function loanDocumentDeleteAction(documentUuid: string): ILoanDocumentDeleteAction {
    return {
        documentUuid,
        type: LoansActionsEnum.LoanDocumentDelete,
    };
}

export interface ILoanDocumentRemoveAction extends Action {
    documentUuid: string;
}

export function loanDocumentRemoveAction(documentUuid: string): ILoanDocumentRemoveAction {
    return {
        documentUuid,
        type: LoansActionsEnum.LoanDocumentRemove,
    };
}

export interface ILoanDrawdownAction extends Action {
    uuid: string;
}

export function loanDrawdownAction(uuid: string): ILoanDrawdownAction {
    return {
        type: LoansActionsEnum.LoanDrawdown,
        uuid,
    };
}

export interface ILoanDrawdownApproveAction extends Action {
    drawdownUuid: string;
}

export function loanDrawdownApproveAction(drawdownUuid: string): ILoanDrawdownApproveAction {
    return {
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownApprove,
    };
}

export interface ILoanDrawdownRejectAction extends Action {
    drawdownUuid: string;
}

export function loanDrawdownRejectAction(drawdownUuid: string): ILoanDrawdownApproveAction {
    return {
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownReject,
    };
}

export interface ILoanDrawdownDisbursalInstructedAction extends Action {
    disbursalInstructedTime: string;
    drawdownUuid: string;
}

export function loanDrawdownDisbursalInstructedAction(drawdownUuid: string, disbursalInstructedTime: string): ILoanDrawdownDisbursalInstructedAction {
    return {
        disbursalInstructedTime,
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownDisbursalInstructed,
    };
}

export interface ILoanDrawdownSubmitForApprovalAction extends Action {
    drawdownUuid: string;
}

export function loanDrawdownSubmitForApprovalAction(drawdownUuid: string): ILoanDrawdownSubmitForApprovalAction {
    return {
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownSubmitForApproval,
    };
}

export interface ILoanDrawdownProcessAction extends Action {
    drawdownUuid: string;
    processedTime: string;
}

export function loanDrawdownProcessAction(drawdownUuid: string, processedTime: string): ILoanDrawdownProcessAction {
    return {
        drawdownUuid,
        processedTime,
        type: LoansActionsEnum.LoanDrawdownProcess,
    };
}

export interface ILoanDrawdownGetAction extends Action {
    drawdownUuid: string;
}

export function loanDrawdownGetAction(drawdownUuid: string): ILoanDrawdownGetAction {
    return {
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownGet,
    };
}

export interface ILoanDrawdownQsInstructedAction extends Action {
    drawdownUuid: string;
    qsInstructedTime: string;
}

export function loanDrawdownQsInstructedAction(drawdownUuid: string, qsInstructedTime: string): ILoanDrawdownQsInstructedAction {
    return {
        drawdownUuid,
        qsInstructedTime,
        type: LoansActionsEnum.LoanDrawdownQsInstructed,
    };
}

export interface ILoanDrawdownQsReceivedAction extends Action {
    drawdownUuid: string;
    qsCertifiedAmount: number;
    qsCompleteAmount: number;
    qsReceivedTime: string;
}

export function loanDrawdownQsReceivedAction(drawdownUuid: string, qsReceivedTime: string, qsCertifiedAmount: number, qsCompleteAmount: number): ILoanDrawdownQsReceivedAction {
    return {
        drawdownUuid,
        qsCertifiedAmount,
        qsCompleteAmount,
        qsReceivedTime,
        type: LoansActionsEnum.LoanDrawdownQsReceived,
    };
}

export interface ILoanDrawdownItemAddAction extends Action {
    item: ILoanDrawdownItem;
}

export function loanDrawdownItemAddAction(item: ILoanDrawdownItem): ILoanDrawdownItemAddAction {
    return {
        item,
        type: LoansActionsEnum.LoanDrawdownItemsAdd,
    };
}

export interface ILoanDrawdownItemDeleteAction extends Action {
    itemUuid: string;
}

export function loanDrawdownItemDeleteAction(itemUuid: string): ILoanDrawdownItemDeleteAction {
    return {
        itemUuid,
        type: LoansActionsEnum.LoanDrawdownItemDelete,
    };
}

export interface ILoanDrawdownItemRemoveAction extends Action {
    itemUuid: string;
}

export function loanDrawdownItemRemoveAction(itemUuid: string): ILoanDrawdownItemRemoveAction {
    return {
        itemUuid,
        type: LoansActionsEnum.LoanDrawdownItemRemove,
    };
}

export interface ILoanDrawdownItemSendAction extends Action {
    itemUuid: string;
}

export function loanDrawdownItemSendAction(itemUuid: string): ILoanDrawdownItemSendAction {
    return {
        itemUuid,
        type: LoansActionsEnum.LoanDrawdownItemSend,
    };
}

export interface ILoanDrawdownItemSetAction extends Action {
    drawdownItem: ILoanDrawdownItem;
    drawdownUuid: string;
}

export function loanDrawdownItemSetAction(drawdownUuid: string, drawdownItem: ILoanDrawdownItem): ILoanDrawdownItemSetAction {
    return {
        drawdownItem,
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownItemSet,
    };
}

export interface ILoanDrawdownItemValueSetAction extends Action {
    itemUuid: string;
    key: keyof ILoanDrawdownItem;
    value: number | string;
}

export function loanDrawdownItemValueSetAction(itemUuid: string, key: keyof ILoanDrawdownItem, value: number | string): ILoanDrawdownItemValueSetAction {
    return {
        itemUuid,
        key,
        type: LoansActionsEnum.LoanDrawdownItemValueSet,
        value,
    };
}

export interface ILoanDrawdownSetAction extends Action {
    drawdown: ILoanDrawdown;
}

export function loanDrawdownSetAction(drawdown: ILoanDrawdown): ILoanDrawdownSetAction {
    return {
        drawdown,
        type: LoansActionsEnum.LoanDrawdownSet,
    };
}

export interface ILoanDrawdownsAddAction extends Action {
    loanUuid: string;
    requestedAmount: number;
    requestedTime: string;
}

export function loanDrawdownsAddAction(loanUuid: string, requestedAmount: number, requestedTime: string): ILoanDrawdownsAddAction {
    return {
        loanUuid,
        requestedAmount,
        requestedTime,
        type: LoansActionsEnum.LoanDrawdownsAdd,
    };
}

export interface ILoanDrawdownsListAction extends Action {
    loanUuid: string;
}

export function loanDrawdownsListAction(loanUuid: string): ILoanDrawdownsListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanDrawdownsList,
    };
}

export interface ILoanDrawdownsSetAction extends Action {
    drawdowns: ILoanDrawdown[];
    loanUuid: string;
}

export function loanDrawdownsSetAction(loanUuid: string, drawdowns: ILoanDrawdown[]): ILoanDrawdownsSetAction {
    return {
        drawdowns,
        loanUuid,
        type: LoansActionsEnum.LoanDrawdownsSet,
    };
}

export interface ILoanDrawdownSendAction extends Action {
    drawdownUuid: string;
}

export function loanDrawdownSendAction(drawdownUuid: string): ILoanDrawdownSendAction {
    return {
        drawdownUuid,
        type: LoansActionsEnum.LoanDrawdownSend,
    };
}

export interface ILoanDrawdownValueSetAction extends Action {
    drawdownUuid: string;
    key: keyof ILoanDrawdown;
    value: number | string;
}

export function loanDrawdownValueSetAction(drawdownUuid: string, key: keyof ILoanDrawdown, value: number | string): ILoanDrawdownValueSetAction {
    return {
        drawdownUuid,
        key,
        type: LoansActionsEnum.LoanDrawdownValueSet,
        value,
    };
}

export interface ILoanExtensionAction extends Action {
    uuid: string;
}

export function loanExtensionAction(uuid: string): ILoanExtensionAction {
    return {
        type: LoansActionsEnum.LoanExtension,
        uuid,
    };
}

export interface ILoanFeeDeleteAction extends Action {
    feeUuid: string;
    loanUuid: string;
}

export function loanFeeDeleteAction(loanUuid: string, feeUuid: string): ILoanFeeDeleteAction {
    return {
        feeUuid,
        loanUuid,
        type: LoansActionsEnum.LoanFeeDelete,
    };
}

export interface ILoanFeeRemoveAction extends Action {
    feeUuid: string;
    loanUuid: string;
}

export function loanFeeRemoveAction(loanUuid: string, feeUuid: string): ILoanFeeRemoveAction {
    return {
        feeUuid,
        loanUuid,
        type: LoansActionsEnum.LoanFeeRemove,
    };
}

export interface ILoanFeeSendAction extends Action {
    feeUuid: string;
    loanUuid: string;
}

export function loanFeeSendAction(loanUuid: string, feeUuid: string): ILoanFeeSendAction {
    return {
        feeUuid,
        loanUuid,
        type: LoansActionsEnum.LoanFeeSend,
    };
}

export interface ILoanFeeSetAction extends Action {
    fee: ILoanFee;
    loanUuid: string;
}

export function loanFeeSetAction(loanUuid: string, fee: ILoanFee): ILoanFeeSetAction {
    return {
        fee,
        loanUuid,
        type: LoansActionsEnum.LoanFeeSet,
    };
}

export interface ILoanFeeValueSetAction extends Action {
    feeUuid: string;
    key: keyof ILoanFee;
    loanUuid: string;
    value: boolean | number | string;
}

export function loanFeeValueSetAction(loanUuid: string, feeUuid: string, key: keyof ILoanFee, value: boolean | number | string): ILoanFeeValueSetAction {
    return {
        feeUuid,
        key,
        loanUuid,
        type: LoansActionsEnum.LoanFeeValueSet,
        value,
    };
}

export interface ILoanFeesAddAction extends Action {
    fee: ILoanFee;
    loanUuid: string;
}

export function loanFeesAddAction(loanUuid: string, fee: ILoanFee): ILoanFeesAddAction {
    return {
        fee,
        loanUuid,
        type: LoansActionsEnum.LoanFeesAdd,
    };
}

export interface ILoanFeesListAction extends Action {
    loanUuid: string;
}

export function loanFeesListAction(loanUuid: string): ILoanFeesListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanFeesList,
    };
}

export interface ILoanFeesSetAction extends Action {
    fees: ILoanFee[];
    loanUuid: string;
}

export function loanFeesSetAction(loanUuid: string, fees: ILoanFee[]): ILoanFeesSetAction {
    return {
        fees,
        loanUuid,
        type: LoansActionsEnum.LoanFeesSet,
    };
}

export interface ILoanGetAction extends Action {
    uuid: string;
}

export function loanGetAction(uuid: string): ILoanGetAction {
    return {
        type: LoansActionsEnum.LoanGet,
        uuid,
    };
}

export interface ILoanGracePeriodAction extends Action {
    uuid: string;
}

export function loanGracePeriodAction(uuid: string): ILoanGracePeriodAction {
    return {
        type: LoansActionsEnum.LoanGracePeriod,
        uuid,
    };
}

export interface ILoanGracePeriodSetAction extends Action {
    loanGracePeriod: ILoanGracePeriod;
}

export function loanGracePeriodSetAction(loanGracePeriod: ILoanGracePeriod): ILoanGracePeriodSetAction {
    return {
        loanGracePeriod,
        type: LoansActionsEnum.LoanGracePeriodSet,
    };
}

export interface ILoanGracePeriodsAddAction extends Action {
    loanGracePeriod: ILoanGracePeriod;
    loanUuid: string;
}

export function loanGracePeriodsAddAction(loanUuid: string, loanGracePeriod: ILoanGracePeriod): ILoanGracePeriodsAddAction {
    return {
        loanGracePeriod,
        loanUuid,
        type: LoansActionsEnum.LoanGracePeriodsAdd,
    };
}

export interface ILoanGracePeriodsListAction extends Action {
    loanUuid: string;
}

export function loanGracePeriodsListAction(loanUuid: string): ILoanGracePeriodsListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanGracePeriodsList,
    };
}

export interface ILoanGracePeriodsSetAction extends Action {
    gracePeriods: ILoanGracePeriod[];
    loanUuid: string;
}

export function loanGracePeriodsSetAction(loanUuid: string, gracePeriods: ILoanGracePeriod[]): ILoanGracePeriodsSetAction {
    return {
        gracePeriods,
        loanUuid,
        type: LoansActionsEnum.LoanGracePeriodsSet,
    };
}

export interface ILoanHistoriesListAction extends Action {
    uuid: string;
}

export function loanHistoriesListAction(uuid: string): ILoanHistoriesListAction {
    return {
        type: LoansActionsEnum.LoanHistoriesList,
        uuid,
    };
}

export interface ILoanHistoriesSetAction extends Action {
    histories: IHistory[];
    uuid: string;
}

export function loanHistoriesSetAction(uuid: string, histories: IHistory[]): ILoanHistoriesSetAction {
    return {
        histories,
        type: LoansActionsEnum.LoanHistoriesSet,
        uuid,
    };
}

export interface ILoanInvestmentsListAction extends Action {
    loanUuid: string;
}

export function loanInvestmentsListAction(loanUuid: string): ILoanInvestmentsListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanInvestmentsList,
    };
}

export interface ILoanInvestmentsSetAction extends Action {
    investments: IInvestment[];
    loanUuid: string;
}

export function loanInvestmentsSetAction(loanUuid: string, investments: IInvestment[]): ILoanInvestmentsSetAction {
    return {
        investments,
        loanUuid,
        type: LoansActionsEnum.LoanInvestmentsSet,
    };
}

export interface ILoanListSettingsSetAction extends Action {
    settings: ILoanListSettings;
}

export function loanListSettingsSetAction(settings: ILoanListSettings): ILoanListSettingsSetAction {
    return {
        settings,
        type: LoansActionsEnum.LoanListSettingsSet,
    };
}

export interface ILoanNoteSetAction extends Action {
    loanUuid: string;
    note: ILoanNote;
}

export function loanNoteSetAction(loanUuid: string, note: ILoanNote): ILoanNoteSetAction {
    return {
        loanUuid,
        note,
        type: LoansActionsEnum.LoanNoteSet,
    };
}

export interface ILoanNotesAddAction extends Action {
    loanUuid: string;
    note: string;
}

export function loanNotesAddAction(loanUuid: string, note: string): ILoanNotesAddAction {
    return {
        loanUuid,
        note,
        type: LoansActionsEnum.LoanNotesAdd,
    };
}

export interface ILoanNotesListAction extends Action {
    uuid: string;
}

export function loanNotesListAction(uuid: string): ILoanNotesListAction {
    return {
        type: LoansActionsEnum.LoanNotesList,
        uuid,
    };
}

export interface ILoanNotesSetAction extends Action {
    notes: ILoanNote[];
    uuid: string;
}

export function loanNotesSetAction(uuid: string, notes: ILoanNote[]): ILoanNotesSetAction {
    return {
        notes,
        type: LoansActionsEnum.LoanNotesSet,
        uuid,
    };
}

export interface ILoanPayoutFigureApproveAction extends Action {
    payoutFigureUuid: string;
}

export function loanPayoutFigureApproveAction(payoutFigureUuid: string): ILoanPayoutFigureApproveAction {
    return {
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureApprove,
    };
}

export interface ILoanPayoutFigureCalculationAction extends Action {
    dischargeDate: string;
    loanUuid: string;
    payoutFigureUuid?: string;
}

export function loanPayoutFigureCalculationAction(loanUuid: string, dischargeDate: string, payoutFigureUuid?: string): ILoanPayoutFigureCalculationAction {
    return {
        dischargeDate,
        loanUuid,
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureCalculation,
    };
}

export interface ILoanPayoutFigureCalculationSetAction extends Action {
    dischargeDate: string;
    loanPayoutFigureCalculation: ILoanPayoutFigureCalculation;
    loanUuid: string;
}

export function loanPayoutFigureCalculationSetAction(loanUuid: string, dischargeDate: string, loanPayoutFigureCalculation: ILoanPayoutFigureCalculation): ILoanPayoutFigureCalculationSetAction {
    return {
        dischargeDate,
        loanPayoutFigureCalculation,
        loanUuid,
        type: LoansActionsEnum.LoanPayoutFigureCalculationSet,
    };
}

export interface ILoanPayoutFigureCloneAction extends Action {
    payoutFigureUuid: string;
}

export function loanPayoutFigureCloneAction(payoutFigureUuid: string): ILoanPayoutFigureCloneAction {
    return {
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureClone,
    };
}

export interface ILoanPayoutFigureDeleteAction extends Action {
    payoutFigureUuid: string;
}

export function loanPayoutFigureDeleteAction(payoutFigureUuid: string): ILoanPayoutFigureDeleteAction {
    return {
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureDelete,
    };
}

export interface ILoanPayoutFigureDischargeDateAction extends Action {
    dischargeDate: string;
    payoutFigureUuid: string;
}

export function loanPayoutFigureDischargeDateAction(payoutFigureUuid: string, dischargeDate: string): ILoanPayoutFigureDischargeDateAction {
    return {
        dischargeDate,
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureDischargeDate,
    };
}

export interface ILoanPayoutFigureRemoveAction extends Action {
    payoutFigureUuid: string;
}

export function loanPayoutFigureRemoveAction(payoutFigureUuid: string): ILoanPayoutFigureRemoveAction {
    return {
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureRemove,
    };
}

export interface ILoanPayoutFigureGetAction extends Action {
    payoutFigureUuid: string;
}

export function loanPayoutFigureGetAction(payoutFigureUuid: string): ILoanPayoutFigureGetAction {
    return {
        payoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureGet,
    };
}

export interface ILoanPayoutFigureGetWithLoanAndApplicationAction extends Action {
    loanPayoutFigureUuid: string;
}

export function loanPayoutFigureGetWithLoanAndApplicationAction(loanPayoutFigureUuid: string): ILoanPayoutFigureGetWithLoanAndApplicationAction {
    return {
        loanPayoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureGetWithLoanAndApplication,
    };
}

export interface ILoanPayoutFigureProcessAction extends Action {
    force: boolean;
    payoutFigureUuid: string;
    sendEmail: boolean;
}

export function loanPayoutFigureProcessAction(payoutFigureUuid: string, sendEmail: boolean, force: boolean = false): ILoanPayoutFigureProcessAction {
    return {
        force,
        payoutFigureUuid,
        sendEmail,
        type: LoansActionsEnum.LoanPayoutFigureProcess,
    };
}

export interface ILoanPayoutFigureSendAction extends Action {
    loanPayoutFigureUuid: string;
}

export function loanPayoutFigureSendAction(loanPayoutFigureUuid: string): ILoanPayoutFigureSendAction {
    return {
        loanPayoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureSend,
    };
}

export interface ILoanPayoutFigureSetAction extends Action {
    payoutFigure: ILoanPayoutFigure;
}

export function loanPayoutFigureSetAction(payoutFigure: ILoanPayoutFigure): ILoanPayoutFigureSetAction {
    return {
        payoutFigure,
        type: LoansActionsEnum.LoanPayoutFigureSet,
    };
}

export interface ILoanPayoutFigureValueSetAction extends Action {
    key: keyof ILoanPayoutFigure;
    loanPayoutFigureUuid: string;
    value: boolean | number | string;
}

export function loanPayoutFigureValueSetAction(loanPayoutFigureUuid: string, key: keyof ILoanPayoutFigure, value: boolean | number | string): ILoanPayoutFigureValueSetAction {
    return {
        key,
        loanPayoutFigureUuid,
        type: LoansActionsEnum.LoanPayoutFigureValueSet,
        value,
    };
}

export interface ILoanPayoutFigureItemAddAction extends Action {
    loanPayoutFigureItem: ILoanPayoutFigureItem;
}

export function loanPayoutFigureItemAddAction(loanPayoutFigureItem: ILoanPayoutFigureItem): ILoanPayoutFigureItemAddAction {
    return {
        loanPayoutFigureItem,
        type: LoansActionsEnum.LoanPayoutFigureItemAdd,
    };
}

export interface ILoanPayoutFigureItemDeleteAction extends Action {
    itemUuid: string;
}

export function loanPayoutFigureItemDeleteAction(itemUuid: string): ILoanPayoutFigureItemDeleteAction {
    return {
        itemUuid,
        type: LoansActionsEnum.LoanPayoutFigureItemDelete,
    };
}

export interface ILoanPayoutFigureItemSendAction extends Action {
    itemUuid: string;
}

export function loanPayoutFigureItemSendAction(itemUuid: string): ILoanPayoutFigureItemSendAction {
    return {
        itemUuid,
        type: LoansActionsEnum.LoanPayoutFigureItemSend,
    };
}

export interface ILoanPayoutFigureItemSetAction extends Action {
    item: ILoanPayoutFigureItem;
}

export function loanPayoutFigureItemSetAction(item: ILoanPayoutFigureItem): ILoanPayoutFigureItemSetAction {
    return {
        item,
        type: LoansActionsEnum.LoanPayoutFigureItemSet,
    };
}

export interface ILoanPayoutFigureItemValueSetAction extends Action {
    itemUuid: string;
    key: keyof ILoanPayoutFigureItem;
    value: boolean | number | string;
}

export function loanPayoutFigureItemValueSetAction(itemUuid: string, key: keyof ILoanPayoutFigureItem, value: boolean | number | string): ILoanPayoutFigureItemValueSetAction {
    return {
        itemUuid,
        key,
        type: LoansActionsEnum.LoanPayoutFigureItemValueSet,
        value,
    };
}

export interface ILoanPayoutFigureSectionAddAction extends Action {
    loanPayoutFigureSection: ILoanPayoutFigureSection;
}

export function loanPayoutFigureSectionAddAction(loanPayoutFigureSection: ILoanPayoutFigureSection): ILoanPayoutFigureSectionAddAction {
    return {
        loanPayoutFigureSection,
        type: LoansActionsEnum.LoanPayoutFigureSectionAdd,
    };
}

export interface ILoanPayoutFigureSectionDeleteAction extends Action {
    sectionUuid: string;
}

export function loanPayoutFigureSectionDeleteAction(sectionUuid: string): ILoanPayoutFigureSectionDeleteAction {
    return {
        sectionUuid,
        type: LoansActionsEnum.LoanPayoutFigureSectionDelete,
    };
}

export interface ILoanPayoutFigureSectionSetAction extends Action {
    section: ILoanPayoutFigureSection;
}

export function loanPayoutFigureSectionSetAction(section: ILoanPayoutFigureSection): ILoanPayoutFigureSectionSetAction {
    return {
        section,
        type: LoansActionsEnum.LoanPayoutFigureSectionSet,
    };
}

export interface ILoanPayoutFiguresAddAction extends Action {
    payoutFigure: ILoanPayoutFigure;
}

export function loanPayoutFiguresAddAction(payoutFigure: ILoanPayoutFigure): ILoanPayoutFiguresAddAction {
    return {
        payoutFigure,
        type: LoansActionsEnum.LoanPayoutFiguresAdd,
    };
}

export interface ILoanPayoutFiguresListAction extends Action {
    loanUuid: string;
}

export function loanPayoutFiguresListAction(loanUuid: string): ILoanPayoutFiguresListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanPayoutFiguresList,
    };
}

export interface ILoanPayoutFiguresSetAction extends Action {
    loanUuid: string;
    payoutFigures: ILoanPayoutFigure[];
}

export function loanPayoutFiguresSetAction(loanUuid: string, payoutFigures: ILoanPayoutFigure[]): ILoanPayoutFiguresSetAction {
    return {
        loanUuid,
        payoutFigures,
        type: LoansActionsEnum.LoanPayoutFiguresSet,
    };
}

export interface ILoanPrincipalReductionAction extends Action {
    amount: number;
    loanUuid: string;
    releaseDealPropertyUuids: string[];
    transactionDate: string;
}

export function loanPrincipalReductionAction(loanUuid: string, amount: number, transactionDate: string, releaseDealPropertyUuids: string[]): ILoanPrincipalReductionAction {
    return {
        amount,
        loanUuid,
        releaseDealPropertyUuids,
        transactionDate,
        type: LoansActionsEnum.LoanPrincipalReduction,
    };
}

export interface ILoanRecordDrawdownAction extends Action {
    loanUuid: string;
    retainedContingency: number;
    retainedFunds: number;
    transactionDate: string;
}

export function loanRecordDrawdownAction(loanUuid: string, retainedContingency: number, retainedFunds: number, transactionDate: string): ILoanRecordDrawdownAction {
    return {
        loanUuid,
        retainedContingency,
        retainedFunds,
        transactionDate,
        type: LoansActionsEnum.LoanRecordDrawdown,
    };
}

export interface ILoanRecordOtherRetainedFundsReductionAction extends Action {
    loanUuid: string;
    otherRetainedFunds: number;
    transactionDate: string;
}

export function loanRecordOtherRetainedFundsReductionAction(loanUuid: string, otherRetainedFunds: number, transactionDate: string): ILoanRecordOtherRetainedFundsReductionAction {
    return {
        loanUuid,
        otherRetainedFunds,
        transactionDate,
        type: LoansActionsEnum.LoanRecordOtherRetainedFundsReduction,
    };
}

export interface ILoanRecordNextPaymentAction extends Action {
    force: boolean;
    uuid: string;
}

export function loanRecordNextPaymentAction(uuid: string, force: boolean): ILoanRecordNextPaymentAction {
    return {
        force,
        type: LoansActionsEnum.LoanRecordNextPayment,
        uuid,
    };
}

export interface ILoanRecoveryAction extends Action {
    loanUuid: string;
}

export function loanRecoveryAction(loanUuid: string): ILoanRecoveryAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanRecovery,
    };
}

export interface ILoanSendAction extends Action {
    loanUuid: string;
}

export function loanSendAction(loanUuid: string): ILoanSendAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanSend,
    };
}

export interface ILoanSendMaturationReminderAction extends Action {
    status: MaturityReminderStatusEnum;
    uuid: string;
}

export function loanSendMaturationReminderAction(uuid: string, status: MaturityReminderStatusEnum): ILoanSendMaturationReminderAction {
    return {
        status,
        type: LoansActionsEnum.LoanSendMaturationReminder,
        uuid,
    };
}

export interface ILoanSkipMaturationReminderAction extends Action {
    status: MaturityReminderStatusEnum;
    uuid: string;
}

export function loanSkipMaturationReminderAction(uuid: string, status: MaturityReminderStatusEnum): ILoanSkipMaturationReminderAction {
    return {
        status,
        type: LoansActionsEnum.LoanSkipMaturationReminder,
        uuid,
    };
}

export interface ILoanSetAction extends Action {
    loan: ILoan;
}

export function loanSetAction(loan: ILoan): ILoanSetAction {
    return {
        loan,
        type: LoansActionsEnum.LoanSet,
    };
}

export interface ILoanTransactionsListAction extends Action {
    loanUuid: string;
}

export function loanTransactionsListAction(loanUuid: string): ILoanTransactionsListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanTransactionsList,
    };
}

export interface ILoanTransactionsSetAction extends Action {
    loanTransactions: ILoanTransaction[];
    loanUuid: string;
}

export function loanTransactionsSetAction(loanUuid: string, loanTransactions: ILoanTransaction[]): ILoanTransactionsSetAction {
    return {
        loanTransactions,
        loanUuid,
        type: LoansActionsEnum.LoanTransactionsSet,
    };
}

export interface ILoanValueSetAction extends Action {
    key: keyof ILoan;
    uuid: string;
    value: boolean | number | string;
}

export function loanValueSetAction(uuid: string, key: keyof ILoan, value: boolean | number | string): ILoanValueSetAction {
    return {
        key,
        type: LoansActionsEnum.LoanValueSet,
        uuid,
        value,
    };
}

export interface ILoanWarehouseLoansListAction extends Action {
    loanUuid: string;
}

export function loanWarehouseLoansListAction(loanUuid: string): ILoanWarehouseLoansListAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanWarehouseLoansList,
    };
}

export interface ILoanWarehouseLoansSetAction extends Action {
    loanUuid: string;
    warehouseLoans: IWarehouseLoan[];
}

export function loanWarehouseLoansSetAction(loanUuid: string, warehouseLoans: IWarehouseLoan[]): ILoanWarehouseLoansSetAction {
    return {
        loanUuid,
        type: LoansActionsEnum.LoanWarehouseLoansSet,
        warehouseLoans,
    };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ILoansDashboardListAction extends Action { }

export function loansDashboardListAction(): ILoansDashboardListAction {
    return {
        type: LoansActionsEnum.LoansDashboardList,
    };
}

export interface ILoansDashboardSetAction extends Action {
    loans: ILoan[];
}

export function loansDashboardSetAction(loans: ILoan[]): ILoansDashboardSetAction {
    return {
        loans,
        type: LoansActionsEnum.LoansDashboardSet,
    };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ILoansDischargeForecastListAction extends Action { }

export function loansDischargeForecastListAction(): ILoansDischargeForecastListAction {
    return {
        type: LoansActionsEnum.LoansDischargeForecastList,
    };
}

export interface ILoansDischargeForecastSetAction extends Action {
    loans: ILoan[];
}

export function loansDischargeForecastSetAction(loans: ILoan[]): ILoansDischargeForecastSetAction {
    return {
        loans,
        type: LoansActionsEnum.LoansDischargeForecastSet,
    };
}

export type ILoansDrawdownsListAction = Action<LoansActionsEnum.LoansDrawdownsList>;

export function loansDrawdownsListAction(): ILoansDrawdownsListAction {
    return {
        type: LoansActionsEnum.LoansDrawdownsList,
    };
}

export interface ILoansDrawdownsSetAction extends Action {
    loans: ILoan[];
}

export function loansDrawdownsSetAction(loans: ILoan[]): ILoansDrawdownsSetAction {
    return {
        loans,
        type: LoansActionsEnum.LoansDrawdownsSet,
    };
}

export type ILoansDrawdownsPendingListAction = Action<LoansActionsEnum.LoansDrawdownsPendingList>;

export function loansDrawdownsPendingListAction(): ILoansDrawdownsPendingListAction {
    return {
        type: LoansActionsEnum.LoansDrawdownsPendingList,
    };
}

export interface ILoansDrawdownsPendingSetAction extends Action {
    drawdowns: ILoanDrawdown[];
}

export function loansDrawdownsPendingSetAction(drawdowns: ILoanDrawdown[]): ILoansDrawdownsPendingSetAction {
    return {
        drawdowns,
        type: LoansActionsEnum.LoansDrawdownsPendingSet,
    };
}

export type ILoansOtherRetainedFundsListAction = Action<LoansActionsEnum.LoansOtherRetainedFundsList>;

export function loansOtherRetainedFundsListAction(): ILoansOtherRetainedFundsListAction {
    return {
        type: LoansActionsEnum.LoansOtherRetainedFundsList,
    };
}

export interface ILoansOtherRetainedFundsSetAction extends Action {
    loans: ILoan[];
}

export function loansOtherRetainedFundsSetAction(loans: ILoan[]): ILoansOtherRetainedFundsSetAction {
    return {
        loans,
        type: LoansActionsEnum.LoansOtherRetainedFundsSet,
    };
}

export interface ILoansPaginatedListAction extends Action {
    administratorUuids: string[];
    code: string;
    name: string;
    order: ListSortOrderEnum;
    orderBy: string;
    page: number;
    perPage: number;
    statuses: LoanStatusEnum[];
}

export function loansPaginatedListAction(
    page: number,
    perPage: number,
    orderBy: string,
    order: ListSortOrderEnum,
    statuses: LoanStatusEnum[],
    administratorUuids: string[],
    code: string,
    name: string,
): ILoansPaginatedListAction {
    return {
        administratorUuids,
        code,
        name,
        order,
        orderBy,
        page,
        perPage,
        statuses,
        type: LoansActionsEnum.LoansPaginatedList,
    };
}

export interface ILoansPaginatedSetAction extends Action {
    loans: ILoan[];
    loansPaginatedCount: number;
    page: number;
    perPage: number;
}

export function loansPaginatedSetAction(loans: ILoan[], loansPaginatedCount: number, page: number, perPage: number): ILoansPaginatedSetAction {
    return {
        loans,
        loansPaginatedCount,
        page,
        perPage,
        type: LoansActionsEnum.LoansPaginatedSet,
    };
}

export interface ILoansSearchAction extends Action {
    keyword: string;
}

export function loansSearchAction(keyword: string): ILoansSearchAction {
    return {
        keyword,
        type: LoansActionsEnum.LoansSearch,
    };
}

export interface ILoansSearchResultsSetAction extends Action {
    loans: ILoan[];
}

export function loansSearchResultsSetAction(loans: ILoan[]): ILoansSearchResultsSetAction {
    return {
        loans,
        type: LoansActionsEnum.LoansSearchResultsSet,
    };
}

export function loansSearchResultsClearAction(): Action {
    return {
        type: LoansActionsEnum.LoansSearchResultsClear,
    };
}

export interface ILoanInternalCommentsAction extends Action {
    internalComments: string;
    loanUuid: string;
}

export function loanInternalCommentsAction(loanUuid: string, internalComments: string): ILoanInternalCommentsAction {
    return {
        internalComments,
        loanUuid,
        type: LoansActionsEnum.LoanInternalComments,
    };
}
